import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { washActivationMethodSchema, washActivationSourceSchema, washActivationStatusSchema, washCreditEntryTypeSchema, washCreditStatusSchema, washCycleStatusSchema, washCycleTriggerSchema, washMachinePaymentTypeSchema, } from "./enums.js";
/**
 * Yıkama İŞLETİMİ (Prompt 18, ADR 0018 §8–11, §18): başlatma (aktivasyon), makine çalışmaları (döngü), self-servis
 * kredileri ve gün kapanışları. Okuma `wash.read`, başlatma / elle döngü `wash.operate`, kredi düzeltme / kilit
 * `wash.manage`. Düz kod ve kod özeti HİÇBİR yanıtta yer almaz (kod bearer'dır).
 */
const secondsString = z
    .string()
    .regex(/^-?\d{1,15}$/)
    .describe("Saniye (dizge, işaretli)");
// ── Aktivasyon ─────────────────────────────────────────────────────────────────
export const washActivationSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    unitId: uuidSchema.nullable(),
    unitName: z.string().nullable(),
    programId: uuidSchema,
    programCode: z.string(),
    programName: z.string(),
    method: washActivationMethodSchema,
    source: washActivationSourceSchema,
    status: washActivationStatusSchema,
    /** Kodla başlatmada (CODE) kod vardır ama YANITTA YOKTUR; yalnızca var olduğu bilgisi. */
    hasCode: z.boolean(),
    expiresAt: timestampSchema.nullable(),
    agentId: uuidSchema.nullable(),
    agentCommandId: uuidSchema.nullable(),
    posSaleId: uuidSchema.nullable(),
    posAgentSaleId: z.string().nullable(),
    posLineNo: z.number().int().nullable(),
    subscriptionId: uuidSchema.nullable(),
    failureReason: z.string().nullable(),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
    startedAt: timestampSchema.nullable(),
    finishedAt: timestampSchema.nullable(),
    version: z.number().int(),
});
export const washActivationPageSchema = pageOf(washActivationSchema);
export const listWashActivationsQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema.optional(),
    unitId: uuidSchema.optional(),
    status: washActivationStatusSchema.optional(),
    source: washActivationSourceSchema.optional(),
});
export const createWashActivationSchema = z.object({
    unitId: uuidSchema.describe("Uzaktan başlatılan (REMOTE) ünite"),
    programId: uuidSchema.describe("Ünitede tanımlı, etkin program"),
});
// ── Döngü (makine çalışması) ───────────────────────────────────────────────────
export const washCycleSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    unitId: uuidSchema.nullable(),
    unitName: z.string().nullable(),
    unitRef: z.string().nullable(),
    programId: uuidSchema.nullable(),
    programCode: z.string(),
    programName: z.string().nullable(),
    trigger: washCycleTriggerSchema,
    status: washCycleStatusSchema,
    startedAt: timestampSchema,
    endedAt: timestampSchema,
    durationSeconds: z.number().int(),
    localDay: localDateSchema,
    activationId: uuidSchema.nullable(),
    creditId: uuidSchema.nullable(),
    secondsUsed: z.number().int().nullable(),
    /** Yalnızca makinenin kendi tahsilatında (KDV dahil, kuruş). */
    amount: moneySchema.nullable(),
    paymentType: washMachinePaymentTypeSchema.nullable(),
    workOrderId: uuidSchema.nullable(),
    warnings: z.array(z.string()),
    dayClosingId: uuidSchema.nullable(),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
});
export const washCyclePageSchema = pageOf(washCycleSchema);
export const listWashCyclesQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema,
    from: localDateSchema.optional().describe("İş günü (bitişin İstanbul günü), dahil"),
    to: localDateSchema.optional(),
    unitId: uuidSchema.optional(),
    trigger: washCycleTriggerSchema.optional(),
    status: washCycleStatusSchema.optional(),
});
export const createManualWashCycleSchema = z
    .object({
    unitId: uuidSchema.describe("Başlatma biçimi MANUAL olan ünite"),
    programId: uuidSchema,
    startedAt: timestampSchema,
    endedAt: timestampSchema,
    status: washCycleStatusSchema.default("COMPLETED"),
})
    .refine((value) => new Date(value.endedAt).getTime() >= new Date(value.startedAt).getTime(), {
    message: "Bitiş başlangıçtan önce olamaz",
    path: ["endedAt"],
});
// ── Self-servis kredisi ────────────────────────────────────────────────────────
export const washCreditSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string(),
    /** Kalıcı kart numarası maskeli (son 4 hane); kod ve özeti HİÇ dönmez. */
    cardNoMasked: z.string().nullable(),
    balanceSeconds: secondsString,
    status: washCreditStatusSchema,
    expiresAt: timestampSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
});
export const washCreditPageSchema = pageOf(washCreditSchema);
export const washCreditEntrySchema = z.object({
    id: uuidSchema,
    entryType: washCreditEntryTypeSchema,
    seconds: secondsString,
    occurredAt: timestampSchema,
    unitId: uuidSchema.nullable(),
    cycleId: uuidSchema.nullable(),
    posSaleId: uuidSchema.nullable(),
    posAgentSaleId: z.string().nullable(),
    posLineNo: z.number().int().nullable(),
    reason: z.string().nullable(),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
});
export const washCreditDetailSchema = washCreditSchema.extend({
    /** Defter (en yeni önce, en çok 500 satır) — bakiye = satırların toplamı. */
    entries: z.array(washCreditEntrySchema),
});
export const listWashCreditsQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema.optional(),
    status: washCreditStatusSchema.optional(),
});
export const adjustWashCreditSchema = z.object({
    seconds: z
        .number()
        .int()
        .min(-10_000_000)
        .max(10_000_000)
        .refine((value) => value !== 0, "Düzeltme sıfır olamaz")
        .describe("İşaretli saniye (+ ekle, − düş)"),
    reason: z.string().trim().min(3).max(500),
    version: z.number().int().min(1),
});
export const washCreditActionSchema = z.object({
    reason: z.string().trim().min(3).max(500).optional(),
    version: z.number().int().min(1),
});
// ── Gün kapanışı ───────────────────────────────────────────────────────────────
export const washDayClosingSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    businessDate: localDateSchema,
    cycleCount: z.number().int(),
    consumptionValue: moneySchema.describe("Kimyasal tüketimi (stok defteri değeri, kuruş)"),
    machineRevenueGross: moneySchema.describe("Makinenin kendi tahsilatı (KDV dahil, kuruş)"),
    consumptionJournalEntryId: uuidSchema.nullable(),
    revenueJournalEntryId: uuidSchema.nullable(),
    status: z.enum(["POSTED", "QUEUED", "EMPTY"]),
    warnings: z.array(z.string()),
    closedBy: uuidSchema.nullable(),
    closedAt: timestampSchema,
});
export const listWashDayClosingsQuerySchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const washDayClosingListSchema = z.object({ items: z.array(washDayClosingSchema) });
//# sourceMappingURL=operations.js.map